import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Shield, Heart, DollarSign } from 'lucide-react';
import { usePageSeo } from '@/hooks/usePageSeo';

const pricingFaqs = [
  { question: 'Do you charge anything to use PropertyTaxHero?', answer: 'No. PropertyTaxHero is free to use.' },
  { question: 'Are there hidden fees or paid upgrades?', answer: 'No. There are no hidden fees, surprise charges, or premium upgrade traps.' },
  { question: 'Do you sell my data?', answer: 'No. We do not sell your data.' },
  { question: 'How is the site paid for?', answer: 'PropertyTaxHero is supported by sponsors featured at the top of the page.' },
];

const Pricing = () => {
  usePageSeo({
    title: 'PropertyTaxHero Pricing | $0, No Hidden Fees',
    description: 'PropertyTaxHero is free to use. No hidden fees, no premium upgrades, and we do not sell your data. Supported by sponsors at the top of the page.',
    path: '/pricing',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: pricingFaqs.map(f => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
  });

  return (
    <Layout>
      <article className="container py-12 md:py-20 max-w-4xl mx-auto">
        <header className="text-center mb-12 md:mb-16">
          <h1 className="text-4xl md:text-6xl font-bold text-tax-deepBlue mb-4">
            PropertyTaxHero pricing: <span className="text-green-600">$0</span>
          </h1>
          <p className="text-xl md:text-2xl text-tax-neutralGray max-w-2xl mx-auto">
            No hidden fees. No upgrade traps. No selling your data.
          </p>
        </header>

        {/* Pricing Card */}
        <div className="flex justify-center mb-12 md:mb-16">
          <Card className="w-full max-w-sm border-2 border-green-200 shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 mb-4">
                <DollarSign className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-5xl font-bold text-green-600 mb-2">$0</p>
              <p className="text-lg font-semibold text-tax-deepBlue mb-1">Forever Free</p>
              <p className="text-sm text-tax-neutralGray">No credit card required. Ever.</p>
              <Button asChild className="mt-6 w-full bg-tax-deepBlue hover:bg-tax-brightBlue">
                <Link to="/#property-form">Get Your Free Report</Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Main Body */}
        <section className="mb-12 md:mb-16">
          <h2 className="text-2xl md:text-3xl font-semibold text-tax-deepBlue mb-4">PropertyTaxHero is free to use. Really free.</h2>
          <div className="text-lg text-tax-neutralGray space-y-4 leading-relaxed">
            <p>We don't charge homeowners to use the site. There are no hidden fees, no premium upgrades, and no surprise paywalls waiting at the end.</p>
            <p>And just as important, <strong>we do not sell your data</strong>.</p>
            <h3 className="text-xl font-semibold text-tax-deepBlue pt-2">So how is the site paid for?</h3>
            <p>The sponsors featured at the top of the page help support PropertyTaxHero, which allows us to keep the site free for homeowners.</p>
            <p className="font-medium text-tax-deepBlue">Helpful tools. Clear answers. Zero cost.</p>
          </div>
        </section>

        {/* Brand-confusion clarification */}
        <section className="mb-12 md:mb-16 bg-tax-lightGray rounded-lg p-6 border border-tax-borderGray">
          <h2 className="text-xl font-semibold text-tax-deepBlue mb-3">A note about PropertyTaxHero pricing</h2>
          <p className="text-tax-neutralGray leading-relaxed">
            PropertyTaxHero is free to use. If you see another site suggesting that PropertyTaxHero charges homeowners a fee, that is incorrect.
            PropertyTaxHero.com does not charge hidden fees, premium fees, or upgrade fees. The only official PropertyTaxHero website is{' '}
            <a href="https://www.propertytaxhero.com" className="text-tax-brightBlue hover:underline font-medium">propertytaxhero.com</a>.
          </p>
        </section>

        {/* Trust Bullets */}
        <section className="mb-12 md:mb-16">
          <h2 className="text-2xl font-semibold text-tax-deepBlue mb-6">What that means</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: Heart, text: 'Free to use' },
              { icon: Shield, text: 'No hidden fees' },
              { icon: CheckCircle, text: 'No premium upgrades' },
              { icon: Shield, text: 'We do not sell your data' },
            ].map((item, i) => (
              <Card key={i} className="border border-tax-borderGray">
                <CardContent className="flex items-center gap-4 p-5">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-lg font-medium text-tax-deepBlue">{item.text}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-12 md:mb-16">
          <h2 className="text-2xl font-semibold text-tax-deepBlue mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {pricingFaqs.map((faq, i) => (
              <div key={i} className="border-b border-tax-borderGray pb-5">
                <h3 className="text-lg font-semibold text-tax-deepBlue mb-2">{faq.question}</h3>
                <p className="text-tax-neutralGray">{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center bg-tax-lightGray rounded-lg p-8">
          <h2 className="text-2xl font-semibold text-tax-deepBlue mb-3">Ready to appeal your property taxes?</h2>
          <p className="text-tax-neutralGray mb-6">It's free. It takes a few minutes. And it could save you hundreds.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-tax-deepBlue hover:bg-tax-brightBlue"><Link to="/#property-form">Get Started Free</Link></Button>
            <Button asChild variant="outline"><Link to="/guides">View State Guides</Link></Button>
            <Button asChild variant="outline"><Link to="/faq">How It Works</Link></Button>
          </div>
        </section>
      </article>
    </Layout>
  );
};

export default Pricing;
